import moment from 'moment';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { useMediaQuery } from '@react-hook/media-query';
import styled from '@emotion/styled';
import { Button, pxToRem } from '@modernhealth/vital-components';
import { layout, mediaQuery } from '@modernhealth/vital-foundation';

import { appClient } from 'src/config/apollo';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';

import { DailyPauseStatsHeader } from 'src/modules/scheduledContent/components/DailyPauseStatsHeader';
import { DailyPauseStatsTiles } from 'src/modules/scheduledContent/components/DailyPauseStatsTiles';
import { DailyPauseStreaksCalendar } from 'src/modules/scheduledContent/components/DailyPauseStreaksCalendar';
import { MinimizedDailyPauseStats } from 'src/modules/scheduledContent/components/MinimizedDailyPauseStats';
import { MinimizedCompletedDailyPauses } from 'src/modules/scheduledContent/components/MinimizedCompletedDailyPauses';
import { NotificationsModal } from 'src/modules/scheduledContent/components/NotificationsModal';
import { useGetScheduledContentActivityQuery } from 'src/modules/scheduledContent/documents/getScheduledContentActivity.generated';
import { DailyPauseStatsSkeletons } from '../components/DailyPauseSkeletons';
import { capture } from 'src/shared/helpers/sentryCapture';
import { DailyPauseStatsTestId } from './testIds';
import { captureException } from 'src/shared/helpers/captureException';

const Wrapper = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
});

interface FlexWrapperProps {
    flexDirection?: 'column' | 'row';
}

const FlexWrapper = styled.div<FlexWrapperProps>(({ flexDirection = 'column' }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexDirection,

    [mediaQuery.maxWidth('lg')]: {
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        flexDirection,
    },
}));

const LeftColumnContainer = styled(FlexWrapper)({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
});

const RightColumnContainer = styled(FlexWrapper)({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginTop: pxToRem(-6),

    [mediaQuery.maxWidth('lg')]: {
        marginTop: pxToRem(44),
    },
});

const BackButton = styled(Button)({
    marginTop: pxToRem(4),
    marginLeft: pxToRem(-18),
});

export const DailyPauseStats = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const user = useSelector(getUserInformation);
    const [isReminderShown, setIsReminderShown] = useState(false);
    const pastMonth = moment().subtract(1, 'months').format('Y-MM'); // minus 1 month, because in DailyPauseWeekStreak component there can be days from pastMonth
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('lg', true));

    const { data, loading } = useGetScheduledContentActivityQuery({
        variables: { from: `${pastMonth}-01` },
        client: appClient,
        onError: error => {
            capture(error);
            captureException(error);
        },
        fetchPolicy: 'cache-and-network',
    });

    const streakDates = useMemo(
        () => data?.scheduledContentActivity?.myStreakTracking.map(item => item.date),
        [data?.scheduledContentActivity?.myStreakTracking],
    );

    if (loading && !data?.scheduledContentActivity) {
        return <DailyPauseStatsSkeletons />;
    }

    if (!loading && !data?.scheduledContentActivity) {
        return <Redirect to="/dashboard" />;
    }

    const headerText =
        data?.scheduledContentActivity?.completedCount === 1
            ? t('dailyPause:streaks.stats.title')
            : t('dailyPause:streaks.stats.title_plural', { name: user.firstName });

    const completedCount = data?.scheduledContentActivity?.completedCount ?? 0;
    const weekStreakCount = data?.scheduledContentActivity?.weekStreakCount;
    const dayStreakCount = data?.scheduledContentActivity?.dayStreakCount;

    const setReminderModal = () => {
        setIsReminderShown(true);
    };

    return (
        <Wrapper data-testid={DailyPauseStatsTestId.Container}>
            <BackButton
                variant="flat"
                size="medium"
                icon="arrow-left"
                onClick={() => {
                    history.push('/dashboard');
                }}
            />
            {!smallScreenDetected && (
                <DailyPauseStatsHeader
                    headerText={headerText}
                    weekStreak={weekStreakCount}
                    dayStreak={dayStreakCount}
                    onClick={setReminderModal}
                />
            )}
            <FlexWrapper flexDirection={smallScreenDetected ? 'column' : 'row'}>
                <LeftColumnContainer>
                    {smallScreenDetected && (
                        <DailyPauseStatsHeader
                            headerText={headerText}
                            weekStreak={weekStreakCount}
                            dayStreak={dayStreakCount}
                            onClick={setReminderModal}
                        />
                    )}
                    <DailyPauseStatsTiles
                        completedCount={completedCount}
                        weekStreak={weekStreakCount}
                    />
                    <DailyPauseStreaksCalendar completed={streakDates || []} />
                </LeftColumnContainer>
                <RightColumnContainer css={{ paddingLeft: smallScreenDetected ? 0 : pxToRem(20) }}>
                    <MinimizedDailyPauseStats completedCount={completedCount} />
                    <MinimizedCompletedDailyPauses />
                </RightColumnContainer>
            </FlexWrapper>
            <NotificationsModal
                isVisible={isReminderShown}
                onClose={() => setIsReminderShown(false)}
            />
        </Wrapper>
    );
};
